// ============================================================
// Generación, normalización y cifrado (hash) de los códigos de acceso
// anónimo al cuestionario de riesgo psicosocial — funciones puras salvo
// `sha256Hex` y el valor por defecto de la fuente aleatoria
// (`defaultRandomSource`), que usan la API de crypto del navegador
// (Web Crypto API: crypto.getRandomValues / crypto.subtle.digest).
//
// CITA REAL (fuente): ninguna norma del MDT fija el formato del código
// de acceso — esto es un mecanismo propio para que el trabajador
// responda el cuestionario de forma anónima sin cuenta de usuario.
//
// DECISIÓN PROPIA (implementación, no de la fuente — pendiente de
// validar con Kevin):
//   - Alfabeto de 32 caracteres sin caracteres confundibles (sin I, O,
//     0, 1) para que el código se pueda transcribir a mano con bajo
//     riesgo de error.
//   - Longitud 16 caracteres = 80 bits de entropía (log2(32^16)).
//   - Tope de 1000 códigos por lote (`generateCodes`).
//   - Los caracteres confundibles NO se sustituyen automáticamente: si
//     el usuario escribe una 'O' o un '0', el código se considera
//     inválido en vez de intentar adivinar cuál quiso decir.
//
// CONTRATO CON LA EDGE FUNCTION (paso posterior) y con la base de datos
// (migración 20260920000000: `code_hash ~ '^[0-9a-f]{64}$'`): la función
// pública debe normalizar el código exactamente igual que aquí (quitar
// espacios y guiones, pasar a mayúsculas, validar que tenga 16
// caracteres del alfabeto) y aplicar SHA-256 en hexadecimal minúscula al
// código YA NORMALIZADO. El código en claro nunca se guarda ni se
// registra en ningún log — solo su hash.
// ============================================================

export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 16;

export type RandomSource = (byteCount: number) => Uint8Array;

export function defaultRandomSource(byteCount: number): Uint8Array {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error('crypto.getRandomValues no está disponible en este entorno');
  }
  return crypto.getRandomValues(new Uint8Array(byteCount));
}

export function codeFromBytes(bytes: Uint8Array): string {
  if (bytes.length !== CODE_LENGTH) {
    throw new Error(`codeFromBytes espera exactamente ${CODE_LENGTH} bytes, recibió ${bytes.length}`);
  }
  let code = '';
  for (let i = 0; i < bytes.length; i++) {
    code += CODE_ALPHABET[bytes[i] & 31];
  }
  return code;
}

export function generateCodes(
  count: number,
  source: RandomSource = defaultRandomSource,
  maxAttempts?: number
): string[] {
  if (!Number.isInteger(count) || count < 1 || count > 1000) {
    throw new Error(`count inválido: ${count} (debe ser un entero entre 1 y 1000)`);
  }
  const limit = maxAttempts ?? count * 20;

  const codes: string[] = [];
  const seen: { [code: string]: true } = {};
  let attempts = 0;

  while (codes.length < count) {
    if (attempts >= limit) {
      throw new Error(
        `Se agotaron los ${limit} intentos permitidos generando códigos (${codes.length} de ${count} generados)`
      );
    }
    attempts++;
    const bytes = source(CODE_LENGTH);
    const code = codeFromBytes(bytes);
    if (seen[code]) continue;
    seen[code] = true;
    codes.push(code);
  }

  return codes;
}

export function formatCode(normalized: string): string {
  const check = normalizeCode(normalized);
  if (check === null || check !== normalized) {
    throw new Error(`formatCode espera un código ya normalizado y válido, recibió: ${normalized}`);
  }
  const blocks: string[] = [];
  for (let i = 0; i < normalized.length; i += 4) {
    blocks.push(normalized.slice(i, i + 4));
  }
  return blocks.join('-');
}

export function normalizeCode(input: string): string | null {
  if (typeof input !== 'string') return null;
  const stripped = input.replace(/[\s-]/g, '').toUpperCase();
  if (stripped.length !== CODE_LENGTH) return null;
  for (let i = 0; i < stripped.length; i++) {
    if (CODE_ALPHABET.indexOf(stripped[i]) === -1) return null;
  }
  return stripped;
}

export async function sha256Hex(text: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle || typeof crypto.subtle.digest !== 'function') {
    throw new Error('crypto.subtle no está disponible en este entorno');
  }
  const bytes = new TextEncoder().encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
  const hashBytes = new Uint8Array(hashBuffer);
  let hex = '';
  for (let i = 0; i < hashBytes.length; i++) {
    hex += hashBytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

export async function hashCode(input: string): Promise<string> {
  const normalized = normalizeCode(input);
  if (normalized === null) {
    throw new Error('Código inválido');
  }
  return sha256Hex(normalized);
}
